import { findLast, sumBy } from "lodash";

import { Model } from "@vuex-orm/core";
import type { Fields, Query } from "@vuex-orm/core";
import { v4 as uuidv4 } from "uuid";
import { formatSecondsAsString } from "@modules/helpers";
// eslint-disable-next-line import/no-cycle
import Video from "@models/Video";
// eslint-disable-next-line import/no-cycle
import { Action, Click, Input } from "@models/ActionHierarchy";
// eslint-disable-next-line import/no-cycle
import AudioRecording from "@models/AudioRecording";

export interface CachedValues {
  hasRecording: boolean;
  isAudio: boolean;
  hasAudio: boolean;
  isVideo: boolean;
  hasVideo: boolean;
}

export default class VoiceOver extends Model {
  // This is the name used as module name of the Vuex Store.
  public static entity = "voiceOvers";

  // List of all fields (schema) of the post model. `this.attr` is used
  // for the generic field type. The argument is the default value.
  public static fields(): Fields {
    return {
      id: this.uid(() => uuidv4()),
      text: this.string(""),
      description: this.string(""),
      startAtId: this.string(""),
      endAtId: this.string(""),
      // firstAction: this.belongsTo(Action, "startAtId"),
      length: this.number(0),
      videoId: this.number(-1),
      video: this.belongsTo(Video, "videoId"),
      selectedAudioRecordingId: this.number(-1),
      audioRecordings: this.hasMany(AudioRecording, "voiceOverId"),
      // actionIds: this.attr(null),
      actions: this.hasMany(Action, "voiceOverId"),
      // actions: this.hasManyBy(Action, "actionIds"),
      hasRecording: this.boolean(false),
      hasAudio: this.boolean(false),
      isAudio: this.boolean(false),
      hasVideo: this.boolean(false),
      isVideo: this.boolean(false),
    };
  }

  public declare id: string;

  public declare text: string;

  public declare description: string;

  public declare startAtId: string;

  public declare endAtId: string;

  public declare length: number;

  public declare videoId: number;

  public declare video: Video;

  public declare selectedAudioRecordingId: number;

  public declare audioRecordings: AudioRecording[];

  public declare actions: Action[];

  public declare hasRecording: boolean;

  public declare hasAudio: boolean;

  public declare isAudio: boolean;

  public declare hasVideo: boolean;

  public declare isVideo: boolean;

  public async setEndAtId(allActions: Action[]): Promise<string | void> {
    const firstAction = allActions.find((action) => action.id === this.startAtId);
    if (!firstAction) return undefined;
    const firstActionIndex = allActions.indexOf(firstAction);
    const lastAction = allActions[firstActionIndex + this.length - 1];
    await this.$update({
      endAtId: lastAction?.id,
    });
    return lastAction?.id;
  }

  public startsAt(action: Action): boolean {
    return action.id === this.startAtId;
  }

  public get duration(): number {
    if (this.selectedAudioRecording) {
      const { duration } = this.selectedAudioRecording;
      if (duration && duration !== Infinity) {
        return duration;
      }
    }
    return 0;
  }

  public get formattedDuration(): string {
    if (this.duration) {
      return formatSecondsAsString(this.duration);
    }
    return "";
  }

  /**
   * @deprecated
   */
  public get firstAction(): Action | null {
    return Action.find(this.startAtId);
  }

  public getStartAtAction(allActions: Action[]): Action | null {
    return allActions.find((a) => a.id === this.startAtId) || null;
  }

  /**
   * @deprecated
   */
  public get lastAction(): Action | null {
    if (this.endAtId) return Action.find(this.endAtId);
    return Action.query()
      .where("videoId", this.videoId)
      .where("index", (this.firstAction?.index || 0) + this.length - 1)
      .first();
  }

  /**
   * @deprecated
   */

  public get firstVisibleAction(): Action | null {
    return this.actionsQuery.where("visible", true).first();
  }

  /**
   * @deprecated
   */
  public get lastVisibleAction(): Action | null {
    return this.actionsQuery.where("visible", true).last();
  }

  /**
   * @deprecated
   */
  public get actionsQuery(): Query<Action> {
    return Action.query()
      .where("videoId", this.videoId)
      .where("index", (i) => i >= (this.firstAction?.index || 0))
      .where("index", (i) => i <= (this.firstAction?.index || 0) + this.length - 1)
      .orderBy("index");
  }

  public calculatedActions(allActions: Action[]): Action[] {
    const firstAction = this.getStartAtAction(allActions);
    if (!firstAction) return [];
    const firstActionPosition = allActions.indexOf(firstAction);
    if (firstActionPosition === -1) return [];
    return allActions.slice(firstActionPosition, firstActionPosition + this.length);
  }

  /**
   * @deprecated
   */
  public get visibleActions(): Action[] {
    return Action.query()
      .where("videoId", this.videoId)
      .where("index", (i) => i >= (this.firstAction?.index || 0))
      .where("index", (i) => i <= (this.firstAction?.index || 0) + this.length - 1)
      .where("visible", true)
      .orderBy("index")
      .all();
  }

  public get totalDurationOfVisibleActions(): number {
    // this would ideally be a private function.
    // TODO: this.visibleActions is slow, better to pass it in as a function argument.
    const actionTimings = this.visibleActions.map((action) => action.timings);
    return sumBy(actionTimings, "total");
  }

  public get waitAfterLastAction(): number {
    return Math.max(0, this.duration - this.totalDurationOfVisibleActions);
  }

  public get selectedAudioRecording(): AudioRecording | null {
    if (this.selectedAudioRecordingId) {
      return AudioRecording.find(this.selectedAudioRecordingId);
    }
    return AudioRecording.query().where("voiceOverId", this.id).first();
  }

  public firstActionOfNextVoiceOver(allActions: Action[]): Action | undefined {
    const lastActionIndex = allActions.findIndex((action) => action.id === this.endAtId);

    return allActions.find((action, index) => index > lastActionIndex && action.voiceOverId);
  }

  public lastActionOfPreviousVoiceOver(allActions: Action[]): Action | undefined {
    const firstActionIndex = allActions.findIndex((action) => action.id === this.startAtId);
    return findLast(allActions, (action, index) =>
      Boolean(index < firstActionIndex && action.voiceOverId),
    );
  }

  public get hasText(): boolean {
    return /\w+/.test(this.text);
  }

  public static calculateRecordingStates(id: string): Partial<VoiceOver> | undefined {
    const voiceOver = VoiceOver.find(id);
    if (!voiceOver) return undefined;
    return {
      hasRecording: AudioRecording.query().where("voiceOverId", id).exists(),
      hasAudio: AudioRecording.query().where("voiceOverId", id).where("isAudio", true).exists(),
      isAudio: voiceOver.selectedAudioRecording?.isAudio ?? false,
      hasVideo: AudioRecording.query().where("voiceOverId", id).where("isVideo", true).exists(),
      isVideo: voiceOver.selectedAudioRecording?.isVideo ?? false,
    };
  }

  public get hasTextOrRecording(): boolean {
    return this.hasText || this.hasRecording;
  }

  public static generateDescription(id: string): string {
    const voiceOver = VoiceOver.find(id);
    if (!voiceOver) return "";
    const actions = voiceOver.visibleActions;
    // if selecting a field and then filling it in, it should be described as just "filling in" the
    // field.
    // so basically we want to skip a Click if the next action is a FillIn on the exact same element.
    const descriptions: string[] = [];
    const actionDescriptions = actions.map((action) => action.description);
    actions.forEach((action, index) => {
      const nextAction = actions[index + 1];
      if (
        action instanceof Click &&
        nextAction instanceof Input &&
        action.elementOuterHtml &&
        action.elementOuterHtml === nextAction?.elementOuterHtml
      ) {
        // ignore this action because the next action is the significant one.
      } else if (action instanceof Input && action.isTextField) {
        descriptions.push(`fill out the ${action.elementDescription}`);
      } else {
        descriptions.push(action.description.charAt(0).toLowerCase() + action.description.slice(1));
      }
    });
    const sentence = new Intl.ListFormat("en").format(descriptions);
    return sentence.charAt(0).toUpperCase() + sentence.slice(1);
  }

  /**
   * Moves the start and end positions of the voiceover to the specified action IDs.
   * @param {Object} options - The options object.
   * @param {string} options.startAtId - The ID of the action to start at.
   * @param {string} options.endAtId - The ID of the action to end at.
   * @param {Action[]} options.allActions - An array of all actions.
   * @returns {Object|null} An object containing the new length, startAtId, and endAtId, or null if no changes were made.
   */
  public setStartAndEnd({
    startAtId,
    endAtId,
    allActions,
  }: { allActions: Action[] } & (
    | {
        endAtId: string;
        startAtId: string;
      }
    | ({ endAtId: string } & { startAtId: string })
  )): { length: number; endAtId?: string; startAtId?: string } | null {
    const firstActionIndex = allActions.findIndex((action) => action.id === startAtId);
    const lastActionIndex = allActions.findIndex((action) => action.id === endAtId);
    const length = lastActionIndex - firstActionIndex + 1;

    const changes: { length: number; startAtId?: string; endAtId?: string } = {
      length,
    };

    if (startAtId) changes.startAtId = startAtId;
    if (endAtId) changes.endAtId = endAtId;

    if (
      this.length === changes.length &&
      (!startAtId || this.startAtId === changes.startAtId) &&
      (!endAtId || this.endAtId === changes.startAtId)
    )
      return null;

    return changes;
  }

  /**
   * Calculates changes to the voice over's `startAtId` and `length` properties
   * @param newStartingAction
   * @param allActions
   */
  public moveStartToAction(
    newStartingAction: Action,
    allActions: Action[],
  ): {
    startAtId: string;
    length: number;
  } | null {
    let moveToAction = newStartingAction;
    const lastActionOfPreviousVoiceOver = this.lastActionOfPreviousVoiceOver(allActions);
    const lastActionOfPreviousVoiceOverPosition = lastActionOfPreviousVoiceOver
      ? allActions.indexOf(lastActionOfPreviousVoiceOver)
      : -1;

    if (
      lastActionOfPreviousVoiceOverPosition !== -1 &&
      lastActionOfPreviousVoiceOverPosition >= allActions.indexOf(newStartingAction)
    ) {
      // Because drags may be throttled, some actions might be skipped. Therefore snapping to the
      // boundary of the previous voiceover creates a more robust dragging UX than only aborting
      // the drag.
      // console.info("Voiceover drag overlaps the previous voiceover; will limit movement.");
      // const startAtAction = this.actions[lastActionOfPreviousVoiceOver.index + 1];
      const newMoveToAction = allActions[lastActionOfPreviousVoiceOverPosition + 1];
      if (newMoveToAction) moveToAction = newMoveToAction;
    }

    const originalAction = this.getStartAtAction(allActions)!;
    const distance = allActions.indexOf(originalAction) - allActions.indexOf(moveToAction);
    const changes = {
      startAtId: moveToAction.id,
      length: Math.max(this.length + distance, 1),
    };
    if (this.length === changes.length && this.startAtId === changes.startAtId) return null;

    return changes;
  }

  /**
   * Calculates changes to the voice over's `endAtId` (and `startAtId` if necessary) and `length` properties
   * @param targetEndingAction
   * @param allActions - ordered array of actions
   */
  public async moveEndToAction(
    targetEndingAction: Action,
    allActions: Action[],
  ): Promise<{ length: number; endAtId?: string; startAtId?: string } | null> {
    let newEndingAction = targetEndingAction;
    const startingAction = this.getStartAtAction(allActions);
    if (!startingAction) return null;
    const firstActionPosition = allActions.indexOf(startingAction);
    const newEndingPosition = allActions.indexOf(newEndingAction);

    let changes:
      | { length: number; endAtId: string }
      | { length: number; startAtId: string }
      | null = null;
    let newLength = newEndingPosition - firstActionPosition + 1;
    // console.log("moveEndToAction() -> newLength: ", newLength);
    if (newLength <= 0) {
      // The length cannot be 0 or negative, but this means the user is trying to move the voiceOver
      // up. That means we want to limit / snap to the boundary of the previous voiceover, like we
      // do in moveStartToAction().
      // Actually, it’s simpler to just let moveStartToAction() handle this:
      const newStartingIndex = firstActionPosition + newLength - 1;
      if (newStartingIndex >= 0) {
        // await this.$update({ length: 1 });
        const newStartingAction = allActions[newStartingIndex];
        if (newStartingAction) {
          // console.info(
          //   `moveEndToAction() -> use moveStartToAction() to move the start and then set the length to 1`,
          //   newStartingAction
          // );
          changes = await this.moveStartToAction(newStartingAction, allActions);
        }
      }
      // console.info("moveEndToAction() -> Limit newLength to 1");
      // Turns out the user somehow tried to move the voiceover to before the timeline, so we’ll
      // just reset the new length to something sensible instead.
      newLength = 1;
    }
    // console.debug("moveEndToAction() -> Back to business as usual, newLength: ", newLength);
    const firstActionOfNextVoiceOver = this.firstActionOfNextVoiceOver(allActions);
    const firstActionOfNextVoiceOverPosition = firstActionOfNextVoiceOver
      ? allActions.indexOf(firstActionOfNextVoiceOver)
      : -1;
    if (
      firstActionOfNextVoiceOver &&
      firstActionOfNextVoiceOverPosition !== -1 &&
      newEndingPosition >= firstActionOfNextVoiceOverPosition
    ) {
      // Because drags may be throttled, some actions might be skipped. Therefore snapping to the
      // boundary of the next voiceover creates a more robust dragging UX than only aborting
      // the drag.
      newLength = firstActionOfNextVoiceOverPosition - firstActionPosition;
      const newEnd = allActions[firstActionOfNextVoiceOverPosition - 1];
      if (newEnd) newEndingAction = newEnd;

      // console.info(
      //   `moveEndToAction() -> Voiceover overlaps the next voiceover. Will limit length to ${newLength} and set end to:`,
      //   newEnd
      // );
    }
    if (this.length === newLength) return changes;

    return { ...changes, length: newLength, endAtId: newEndingAction.id };
  }

  public static queryForVideo(videoId: number): Query<VoiceOver> {
    return VoiceOver.query().where("videoId", videoId);
  }
}
